import * as Sentry from "@sentry/react";
import type { ErrorEvent } from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

import { STORAGE_KEYS } from "@utils/storageKeys";

const stripQuery = (url: string | undefined): string | undefined => {
  if (!url) return url;
  const queryIndex = url.indexOf("?");
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
};

const scrubEvent = (event: ErrorEvent): ErrorEvent => {
  if (event.request) {
    event.request.url = stripQuery(event.request.url);
    delete event.request.query_string;
    delete event.request.cookies;
    delete event.request.data;
    delete event.request.headers;
  }

  if (event.user) {
    // Keep a stable id for grouping; never send name, email, or IP.
    const userId = event.user.id;
    event.user = userId ? { id: userId } : {};
  }

  if (event.breadcrumbs) {
    for (const breadcrumb of event.breadcrumbs) {
      if (breadcrumb.data) {
        if (typeof breadcrumb.data.url === "string") {
          breadcrumb.data.url = stripQuery(breadcrumb.data.url);
        }
        if (typeof breadcrumb.data.from === "string") {
          breadcrumb.data.from = stripQuery(breadcrumb.data.from);
        }
        if (typeof breadcrumb.data.to === "string") {
          breadcrumb.data.to = stripQuery(breadcrumb.data.to);
        }
        delete breadcrumb.data.request_body;
        delete breadcrumb.data.response_body;
        delete breadcrumb.data.body;
        delete breadcrumb.data.variables;
        delete breadcrumb.data.payload;
      }
      if (typeof breadcrumb.message === "string" && breadcrumb.message.includes("?")) {
        breadcrumb.message = stripQuery(breadcrumb.message) ?? breadcrumb.message;
      }
    }
  }

  if (event.extra) {
    delete event.extra.payload;
    delete event.extra.event_data;
    delete event.extra.variables;
    delete event.extra.request_body;
  }

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : undefined;
  if (pathname) {
    event.tags = {
      ...event.tags,
      route: pathname,
    };
  }

  return event;
};

export const setTenantSchemaTag = (schemaName: string) => {
  sessionStorage.setItem(STORAGE_KEYS.TENANT_SCHEMA_NAME, schemaName);
  Sentry.setTag("tenant.name", schemaName);
  Sentry.setContext("tenant", { schema_name: schemaName });
};

export const clearTenantSchemaTag = () => {
  sessionStorage.removeItem(STORAGE_KEYS.TENANT_SCHEMA_NAME);
  Sentry.setTag("tenant.name", undefined);
  Sentry.setContext("tenant", null);
};

const restoreTenantSchemaTag = () => {
  const schemaName = sessionStorage.getItem(STORAGE_KEYS.TENANT_SCHEMA_NAME);
  if (schemaName) {
    Sentry.setTag("tenant.name", schemaName);
    Sentry.setContext("tenant", { schema_name: schemaName });
  }
};

export const initSentry = () => {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;

  if (environment === "development" || !sentryDsn) {
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
    beforeSend(event: ErrorEvent) {
      return scrubEvent(event);
    },
    beforeSendTransaction(event) {
      if (event.request) {
        event.request.url = stripQuery(event.request.url);
        delete event.request.query_string;
      }
      return event;
    },
  });

  restoreTenantSchemaTag();
};

// Initialize on import so wrapCreateBrowserRouter in Router runs after init.
initSentry();

export { Sentry };
